import {Location} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  Input,
  model,
  OnChanges,
  OnDestroy,
  QueryList,
  signal,
  SimpleChanges,
  untracked,
  ViewChildren
} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {RealtimePollingRequestParameters} from '@services/realtime-polling/realtime-polling-request-parameters';
import {RealtimePollingService} from '@services/realtime-polling/realtime-polling.service';
import {EnvironmentConfig} from '@environments/environment';
import {MachineDetailsService} from '@services/dashboard/machine-details.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {NotificationService} from '@services/notification/notification.service';
import {DashboardListCategoryMachines} from '@models/dashboard/machine-list/dashboard-list-category-machines';
import {CategoryColumn} from '@models/adminstration/portal-setup/machine-category/category-column';
import {shareReplay, takeUntil} from 'rxjs/operators';
import {forkJoin, Observable, Subject} from 'rxjs';
import {
  MachineCategoryBaseColumnTypes
} from '@constants/administration/portal-setup/machine-category/machine-category-base-column-types';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {MetaData} from '@models/adminstration/meta-data';
import {
  MachineCategoryDefaultColumnTypes
} from '@constants/administration/portal-setup/machine-category/machine-category-default-column-types';
import {GATEWAYS_AGGREGATED_STATUS} from '@constants/machine/gateways-aggregated-status';
import {TRAFFIC_LIGHT_STATUS} from '@constants/machine/traffic-light/traffic-light-status';
import {SettingsService} from '@services/administration/settings/settings.service';
import {ELASTIC_SEARCH_SORT_ORDER} from '@models/elastic-search/request/elastic-search-sort-order.enum';
import {
  MACHINE_LIST_ITEMS_PER_PAGE_OPTIONS,
  MachineListCategoryState,
  MachineListSortDirection,
} from '@models/dashboard/machine-list/machine-list-view-user-config';
import {MachineListViewStoreInstance} from '@services/dashboard/machine-list-view.store';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {MenuItem} from '@models/sidebar/menu-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {btou} from '@prototypes/global.functions';
import {generateElasticSearchText} from '@services/elastic-search/elastic-search.service';
import {WidgetHelperService} from '@services/pages/widget/widget-helper.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';


const MACHINE_NAME_COLUMN = 'machineName';
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

interface TrafficLightFilterOption {
  status: TRAFFIC_LIGHT_STATUS;
  label: string;
}

const TRAFFIC_LIGHT_FILTER_OPTIONS: TrafficLightFilterOption[] = [
  {status: TRAFFIC_LIGHT_STATUS.RUNNING, label: 'Running'},
  {status: TRAFFIC_LIGHT_STATUS.WARNING, label: 'Warning'},
  {status: TRAFFIC_LIGHT_STATUS.FAILURE, label: 'Error'},
];

type DisplayDetailsMap = Map<number, Map<string, { value: string, style: string }>>;

@Component({
  selector: 'scitis-machine-list-category-container',
  templateUrl: './machine-list-category-container.component.html',
  styleUrls: ['./machine-list-category-container.component.scss'],
  standalone: false
})
export class MachineListCategoryContainerComponent implements OnChanges, OnDestroy {

  @Input()
  public category: MachineCategoryColumnConfiguration = new MachineCategoryColumnConfiguration();

  @Input()
  currentMachineGroupId: string = '';

  @Input()
  isWindowContentVisible: boolean | undefined;

  @Input()
  organizationId: number = -1;

  @Input()
  rootMachineryGroupId: string = '';

  @Input()
  currentLocation: string = '';

  @Input()
  refreshInterval: number = -1;

  @Input()
  whiteListedMachineryGroups: number[] | null = null;

  @Input()
  store: MachineListViewStoreInstance | null = null;

  @Input()
  userRole: Role = new Role(new User());

  public readonly shownMachines = signal<DashboardMachine[]>([]);
  public readonly resultCount = signal<number>(0);
  public readonly isLoading = signal<boolean>(false);
  public readonly isSearchLoading = signal<boolean>(false);
  public readonly expandedMachine = signal<DashboardMachine | null>(null);
  public readonly expandedMetadata = signal<MetaData[] | null>(null);
  public readonly isColumnPickerOpen = signal<boolean>(false);
  public readonly searchInputValue = model<string>('');
  public readonly machineNameColumn = signal<CategoryColumn>(new CategoryColumn());
  public readonly categoryColumns = signal<CategoryColumn[]>([]);
  public readonly itemsPerPageOptions: number[] = MACHINE_LIST_ITEMS_PER_PAGE_OPTIONS;
  public readonly trafficLightFilterOptions: TrafficLightFilterOption[] = TRAFFIC_LIGHT_FILTER_OPTIONS;
  public readonly isTrafficLightFilterOpen = signal<boolean>(false);
  public readonly selectedTrafficLightFilter = signal<TRAFFIC_LIGHT_STATUS | null>(null);
  public displayDetails: DisplayDetailsMap = new Map<number, Map<string, { value: string, style: string }>>();

  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  private machineVisibilityState: Map<string, boolean>;
  private displayIndex: Map<number, number>;
  private loadedMachines: DashboardMachine[] = [];
  private elasticResultCount = 0;
  private trafficLightStatuses: Map<number, TRAFFIC_LIGHT_STATUS> = new Map<number, TRAFFIC_LIGHT_STATUS>();
  private GET_SIGNAL_DATA_URL_SUFFIX: string = '/api/web/tenant/:tenant/machine/:machineId/listview/getSignalData?refresh=:refresh';
  private REFRESH_INTERVAL = EnvironmentConfig.PortalSettings.tileRefreshInterval;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private NOT_AVAILABLE = 'N/A';
  private UNKNOWN = 'Unknown';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private streamingSettingsCache$: Observable<{liveDataEnabled: boolean; influxStreamingEnabled: boolean}> | null = null;

  constructor(private translate: TranslateService,
              private menuNotificationService: MenuNotificationService,
              private pollingService: RealtimePollingService,
              private machineDetailsService: MachineDetailsService,
              private loadingIndicator: LoadingIndicatorService,
              private dashboardService: DashboardService,
              private notificationService: NotificationService,
              private translateService: TranslateService,
              private widgetHelperService: WidgetHelperService,
              private router: Router,
              private location: Location,
              private leftMenuService: LeftMenuService,
              private elasticMetaDataService: MetadataElasticSearchService,
              private tableHeaderSortableService: TableHeaderSortableService,
              private settingsService: SettingsService,
              private changeDetector: ChangeDetectorRef) {
    this.machineVisibilityState = new Map<string, boolean>();
    this.displayIndex = new Map<number, number>();

    effect(() => {
      const value = this.searchInputValue();
      const normalizedValue = value.trim();
      const nextSearchText = normalizedValue.length >= SEARCH_MIN_LENGTH ? normalizedValue : '';
      const currentStoreSearch = untracked(() => this.categoryState.searchText ?? '');
      if (nextSearchText === currentStoreSearch) {
        this.isSearchLoading.set(false);
        return;
      }
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.isSearchLoading.set(true);
      this.searchDebounceTimer = setTimeout(() => {
        this.store?.setSearchText(nextSearchText);
        this.loadMachinesFromElastic();
      }, SEARCH_DEBOUNCE_MS);
    });
  }

  public get categoryState(): MachineListCategoryState {
    return this.store?.activeCategoryState() ?? {
      sortColumn: MACHINE_NAME_COLUMN,
      sortDirection: '',
      searchText: '',
      currentPage: 1,
      itemsPerPage: 10,
      visibleColumns: [],
    };
  }

  public get paginateControlSettings(): PaginateControlSettings {
    const state = this.categoryState;
    return new PaginateControlSettings({
      id: (this.category.categoryName ?? 'machineListCategory') + '_pagination',
      itemsPerPage: this.getValidItemsPerPage(state.itemsPerPage),
      currentPage: state.currentPage,
      totalItems: this.resultCount(),
    });
  }

  private getValidItemsPerPage(itemsPerPage: number): number {
    return this.itemsPerPageOptions.includes(itemsPerPage)
        ? itemsPerPage
        : this.itemsPerPageOptions[0];
  }

  public get isPaginationAvailable(): boolean {
    return this.resultCount() > 0;
  }

  public get hasNoResults(): boolean {
    return !this.isLoading() && this.shownMachines().length === 0;
  }

  public get visibleCategoryColumns(): CategoryColumn[] {
    const visible = this.categoryState.visibleColumns;
    const columns = this.categoryColumns();
    if (!visible || visible.length === 0) {
      return columns;
    }
    return columns.filter(c => visible.includes(c.name));
  }

  public get expandedMachineId(): number | null {
    return this.store?.expandedMachineId() ?? null;
  }

  public get isExpandedLoading(): boolean {
    return this.store?.isExpandedLoading() ?? false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleColumnsConfiguration(changes);

    if (!changes) {
      return;
    }

    if (changes.category && changes.category.currentValue) {
      const previousCategory = changes.category.previousValue as MachineCategoryColumnConfiguration | undefined;
      if (previousCategory?.categoryName !== this.category.categoryName) {
        this.displayDetails.clear();
        this.trafficLightStatuses.clear();
        this.loadedMachines = [];
        this.elasticResultCount = 0;
        this.selectedTrafficLightFilter.set(null);
        this.isTrafficLightFilterOpen.set(false);
      }
      this.ensureCategoryInitialized();
      this.syncSearchInputValue();
      this.loadMachinesFromElastic();
    }

    if (changes.currentLocation || changes.currentMachineGroupId || changes.whiteListedMachineryGroups) {
      this.resetPageAndReload();
    }

    if (changes.refreshInterval && this.refreshInterval >= 0) {
      this.startDataRetrieval();
    }

    if (changes.isWindowContentVisible) {
      if (this.isWindowContentVisible) {
        this.startDataRetrieval();
      } else {
        console.log('Stop polling for all machines');
        this.stopDataRetrieval(this.shownMachines(), false);
      }
    }
  }

  ngOnDestroy() {
    this.stopDataRetrieval(this.shownMachines());
    this.destroy$.next(true);
    this.destroy$.complete();
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.isSearchLoading.set(false);
  }

  public onListItemClick(machineId: string | number, $event?: Event, openInNewTab: boolean = false): void {
    $event?.stopPropagation();
    const newWindow = openInNewTab ? window.open('', '_blank') : null;
    const machineryGroupId = this.getValidMachineryGroupId();
    if (!machineryGroupId) {
      newWindow?.close();
      return;
    }
    const id = machineryGroupId + '_' + machineId;
    this.dashboardService.getItemMenuItemAndParentMenuItem(id, true).subscribe({
      next: (machineMenuItem) => {
        if (openInNewTab) {
          this.openMenuItemInNewTab(machineMenuItem, newWindow);
        } else {
          this.menuNotificationService.broadcastClickOnDashboard(machineMenuItem);
        }
      },
      error: () => newWindow?.close(),
    });
  }

  private openMenuItemInNewTab(machineMenuItem: MenuItem, newWindow: Window | null): void {
    const machineId = this.leftMenuService.getParentId(machineMenuItem);
    if (!machineId) {
      newWindow?.close();
      return;
    }

    this.leftMenuService.getMenuItems(machineId, false).subscribe({
      next: (menuItems: MenuItem[]) => {
        const interactiveDataAnalysisItem = menuItems
        .map(menuItem => Object.assign(new MenuItem(), menuItem))
        .find(menuItem => menuItem.type === MenuItemType.DATA_VISUALIZATION_PAGE);
        if (!interactiveDataAnalysisItem) {
          newWindow?.close();
          return;
        }

        interactiveDataAnalysisItem.menuItemParent = machineMenuItem;
        this.navigateMenuItemInNewTab(interactiveDataAnalysisItem, newWindow);
      },
      error: () => newWindow?.close(),
    });
  }

  private navigateMenuItemInNewTab(menuItem: MenuItem, newWindow: Window | null): void {
    const commands = this.leftMenuService.getCommands(menuItem, '', undefined);
    if (!commands) {
      newWindow?.close();
      return;
    }

    const routerUrl = this.router.serializeUrl(this.router.createUrlTree(commands));
    const url = new URL(this.location.prepareExternalUrl(routerUrl), window.location.origin).toString();
    if (newWindow) {
      newWindow.location.href = url;
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  public onRowClick(machine: DashboardMachine): void {
    const machineId = machine.id;
    const currentExpanded = this.expandedMachineId;
    if (currentExpanded === machineId) {
      this.store?.setExpandedMachine(null);
      this.expandedMachine.set(null);
      this.expandedMetadata.set(null);
      return;
    }
    this.store?.setExpandedMachine(machineId);
    this.expandedMachine.set(machine);
    this.expandedMetadata.set(null);
    this.loadExpandedDetails(machineId);
  }

  public onRowToggle(machine: DashboardMachine, event: Event): void {
    event.stopPropagation();
    this.onRowClick(machine);
  }

  public getMachineDisplayName(machine: DashboardMachine): string {
    return (this.expandedMachine() ?? machine).type?.trim() ?? '';
  }

  public getMachineLocation(machine: DashboardMachine): string {
    return (this.expandedMachine() ?? machine).location?.trim() ?? '';
  }

  public getMachineAddress(machine: DashboardMachine): string {
    const locationDetails = (this.expandedMachine() ?? machine).locationDetails;
    return locationDetails
        ? this.widgetHelperService.getMachineAddress(locationDetails)
        .replace(/\s*,\s*\n\s*/g, ', ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/,\s*$/, '')
        : '';
  }

  public getMachineTag(machine: DashboardMachine): string {
    return (this.expandedMachine() ?? machine).machineTag?.trim() ?? '';
  }

  public getMachineOrganization(machine: DashboardMachine): string {
    return String((this.expandedMachine() ?? machine).tenant ?? '').trim();
  }

  public getMachineDatasetLocation(machine: DashboardMachine): string {
    return (this.expandedMachine() ?? machine).datasetLocation?.trim() ?? '';
  }

  public isColumnSortable(column: CategoryColumn): boolean {
    return column.type !== MachineCategoryBaseColumnTypes.SIGNAL || column.sortable;
  }

  public isTrafficLightColumn(column: CategoryColumn): boolean {
    return column.name === MachineCategoryDefaultColumnTypes.trafficLightStatus.value;
  }

  public toggleTrafficLightFilter(event: Event): void {
    event.stopPropagation();
    this.isTrafficLightFilterOpen.update(open => !open);
  }

  public selectTrafficLightFilter(status: TRAFFIC_LIGHT_STATUS, event: Event): void {
    event.stopPropagation();
    this.selectedTrafficLightFilter.set(status);
    this.isTrafficLightFilterOpen.set(false);
    this.applyTrafficLightFilter();
    this.startDataRetrieval(this.loadedMachines, true);
  }

  public clearTrafficLightFilter(event: Event): void {
    event.stopPropagation();
    this.selectedTrafficLightFilter.set(null);
    this.isTrafficLightFilterOpen.set(false);
    this.applyTrafficLightFilter();
  }

  public isTrafficLightFilterSelected(status: TRAFFIC_LIGHT_STATUS): boolean {
    return this.selectedTrafficLightFilter() === status;
  }

  public getExpandedRowId(machineId: number): string {
    return `machine-details-${machineId}`;
  }

  public onMachineConfigurationClick(machine: DashboardMachine, event?: Event): void {
    event?.stopPropagation();
    const parameters = {
      id: machine.id,
      userRole: this.userRole,
      tenant: machine.tenant,
    };
    const encodedParams = btou(JSON.stringify(parameters));
    this.router.navigate(['machines', encodedParams]);
  }

  public onMachineLineInViewChange($event: any, machineId: string | number) {
    const {visible} = $event;
    machineId = String(machineId);
    const machine: DashboardMachine | null = this.findMachineById(this.shownMachines(), machineId);
    if (machine === null) {
      return;
    }

    if (visible && this.isWindowContentVisible) {
      const savedVisibility = this.machineVisibilityState.get(machineId);
      if ((savedVisibility == undefined || !savedVisibility) && this.hasPollableColumns()) {
        console.debug(`Machine ${machine.name}. Page visible and tile in view`);
        this.startDataRetrievalForMachine(machine);
        this.machineVisibilityState.set(machineId, true);
      }

    } else {
      console.debug(`Machine ${machine.name}. Tile not view`);
      this.stopDataRetrievalForMachine(machine.id);
      this.machineVisibilityState.set(machineId, false);
    }

  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.isTrafficLightFilterOpen.set(false);

    if (this.isFrontendSortedColumn(column)) {
      if (this.isSortableFrontendColumn(column)) {
        const machinesToSort = this.loadedMachines.length > 0 ? this.loadedMachines : this.shownMachines();
        this.loadedMachines = this.tableHeaderSortableService.sortItemsByValue(
            machinesToSort,
            machine => this.displayDetails.get(machine.id)?.get(column)?.value ?? this.NOT_AVAILABLE,
            direction,
        );
        this.applyTrafficLightFilter();
      }
      return;
    }

    this.store?.setSort(column, direction as MachineListSortDirection);
    this.loadMachinesFromElastic();
  }

  public handlePageChange($event: number) {
    this.stopDataRetrieval(this.shownMachines());
    this.store?.setPage($event);
    this.loadMachinesFromElastic();
  }

  public onItemsPerPageChange(itemsPerPage: number): void {
    this.stopDataRetrieval(this.shownMachines());
    this.store?.setItemsPerPage(itemsPerPage);
    this.loadMachinesFromElastic();
  }

  public toggleColumnPicker($event: Event): void {
    $event.stopPropagation();
    this.isColumnPickerOpen.update(open => !open);
  }

  public isColumnVisible(columnName: string): boolean {
    const visible = this.categoryState.visibleColumns;
    if (!visible || visible.length === 0) {
      return true;
    }
    return visible.includes(columnName);
  }

  public toggleColumnVisibility(columnName: string, $event: Event): void {
    $event.stopPropagation();
    const visible = this.categoryState.visibleColumns?.length > 0
        ? [...this.categoryState.visibleColumns]
        : this.collectAllColumnNames();

    if (columnName === MACHINE_NAME_COLUMN) {
      return;
    }
    const index = visible.indexOf(columnName);
    if (index >= 0) {
      visible.splice(index, 1);
    } else {
      visible.push(columnName);
    }
    this.store?.setVisibleColumns(visible);

    if (this.hasPollableColumns()) {
      this.startDataRetrieval(this.shownMachines(), true);
    } else {
      this.stopDataRetrieval(this.shownMachines(), false);
    }
  }

  private applyTrafficLightFilter(): void {
    const selectedStatus = this.selectedTrafficLightFilter();
    if (selectedStatus) {
      const filteredMachines = this.loadedMachines.filter(machine => this.trafficLightStatuses.get(machine.id) === selectedStatus);
      this.shownMachines.set(filteredMachines);
      this.resultCount.set(filteredMachines.length);
      return;
    }

    this.shownMachines.set([...this.loadedMachines]);
    this.resultCount.set(this.elasticResultCount);
  }

  private resetPageAndReload(): void {
    this.store?.setPage(1);
    this.loadMachinesFromElastic();
  }

  private syncSearchInputValue(): void {
    this.searchInputValue.set(this.categoryState.searchText ?? '');
  }

  private ensureCategoryInitialized(): void {
    if (!this.store || !this.category?.categoryName) {
      return;
    }
    const allColumnNames = this.collectAllColumnNames();
    this.store.ensureCategoryInitialized(this.category.categoryName, allColumnNames);
  }

  private collectAllColumnNames(): string[] {
    const names: string[] = [MACHINE_NAME_COLUMN];
    for (const column of this.category?.categoryColumns ?? []) {
      if (column && column.name && column.name !== 'machineId') {
        names.push(column.name);
      }
    }
    return names;
  }

  private startDataRetrieval(machines: DashboardMachine[] = this.shownMachines(), force: boolean = false): void {
    if (this.refreshInterval < 0 || !this.isWindowContentVisible) {
      return;
    }

    if (machines === undefined || machines.length === 0) {
      return;
    }

    if (!this.hasPollableColumns()) {
      this.stopDataRetrieval(machines, false);
      return;
    }

    for (const machine of machines) {
      if (force || this.machineVisibilityState.get(String(machine.id))) {
        this.startDataRetrievalForMachine(machine);
      }
    }
  }

  private handleColumnsConfiguration(changes: SimpleChanges) {
    if (!changes.category || !changes.category.currentValue) {
      return;
    }

    const currentValue: MachineCategoryColumnConfiguration = changes.category.currentValue;
    const configuredColumns = currentValue.categoryColumns ?? [];
    const machineNameColumn = configuredColumns.find(column => column.name === MACHINE_NAME_COLUMN);

    if (machineNameColumn) {
      this.machineNameColumn.set(machineNameColumn);
    } else {
      const fallbackMachineNameColumn = new CategoryColumn();
      fallbackMachineNameColumn.name = MACHINE_NAME_COLUMN;
      fallbackMachineNameColumn.label = this.translate.instant('Machine name');
      fallbackMachineNameColumn.type = MachineCategoryBaseColumnTypes.DEFAULT;
      this.machineNameColumn.set(fallbackMachineNameColumn);
    }

    this.categoryColumns.set(
        configuredColumns.filter(column => column.name !== MACHINE_NAME_COLUMN),
    );
  }

  private stopDataRetrieval(machines: DashboardMachine[] | undefined, resetVisibility: boolean = true) {
    if (machines == undefined || machines.length === 0) {
      return;
    }
    for (const machine of machines) {
      this.stopDataRetrievalForMachine(machine.id);
      if (resetVisibility) {
        this.machineVisibilityState.set(machine.id + '', false);
      }
    }

  }

  private findMachineById(machines: DashboardMachine[] | undefined, id: string) {
    if (!machines || machines.length === 0) {

      return null;
    }
    for (const machine of machines) {
      if (machine.id + '' === id) {
        return machine;
      }
    }
    return null;

  }

  private getStreamingSettings(): Observable<{ liveDataEnabled: boolean; influxStreamingEnabled: boolean }> {
    if (!this.streamingSettingsCache$) {
      this.streamingSettingsCache$ = forkJoin({
        liveDataEnabled: this.settingsService.isLiveDataStreamingEnabled(),
        influxStreamingEnabled: this.settingsService.isInfluxStreamingEnabled(),
      }).pipe(shareReplay({bufferSize: 1, refCount: false}));
    }
    return this.streamingSettingsCache$;
  }

  private hasPollableColumns(): boolean {
    const visible = this.visibleCategoryColumns;
    return visible.some(col =>
        col.type === MachineCategoryBaseColumnTypes.SIGNAL
        || this.isTrafficLightColumn(col)
        || col.name === MachineCategoryDefaultColumnTypes.gatewayConnectivity.value,
    );
  }

  private startDataRetrievalForMachine(machine: DashboardMachine): void {
    this.getStreamingSettings().subscribe({
      next: ({liveDataEnabled, influxStreamingEnabled}) => {
        if (!liveDataEnabled || !influxStreamingEnabled || this.refreshInterval < 0) {
          return;
        }
        this.stopDataRetrievalForMachine(machine.id);
        this.isLoading.set(true);

        this.startPolling(machine).subscribe({
          next: (notify) => {
            this.handleSignalPollingData(machine, notify);
            if (notify) {
              this.updateGatewayConnectivity(machine, this.categoryColumns(), notify.gateways);
              this.displayIndex.set(machine.id, (this.displayIndex!.get(machine.id) || 0) + 1);
            }
            this.stopLoadingIndicator();
          }, error: (error) => {
            console.error('Error getting dashboard data', JSON.stringify(error));
            this.stopLoadingIndicator();
          }
        });
      },
      error: (error) => {
        console.error('Error checking dashboard data streaming settings', JSON.stringify(error));
      },
    });
  }

  private stopDataRetrievalForMachine(machineId: number) {
    this.pollingService.stop(machineId + '');
  }

  private stopLoadingIndicator(): void {
    this.loadingIndicator.hide();
    this.isLoading.set(false);
  }

  private getValidMachineryGroupId(): string {
    return [this.currentMachineGroupId, this.rootMachineryGroupId]
    .map(groupId => (groupId ?? '').trim())
    .find(groupId => !!groupId && groupId !== 'undefined' && groupId !== 'null') ?? '';
  }

  private loadMachinesFromElastic() {
    if (!this.category?.categoryName) {
      this.isSearchLoading.set(false);
      return;
    }
    this.loadingIndicator.show();
    this.isLoading.set(true);
    const machineryGroupId = this.getValidMachineryGroupId();

    const state = this.categoryState;
    const itemsPerPage = this.getValidItemsPerPage(state.itemsPerPage);
    const sortValue = this.buildSortValue(state.sortColumn, state.sortDirection);

    const machinesObservable = this.dashboardService.getDashboardListMachinesFromElastic(
        machineryGroupId,
        this.organizationId,
        this.currentLocation,
        this.category.categoryName,
        sortValue,
        state.currentPage,
        itemsPerPage,
        this.whiteListedMachineryGroups,
        this.buildSearchText(state.searchText)
    );
    machinesObservable.subscribe({
      next: (success: DashboardListCategoryMachines) => {
        const machines = Array.from(success.machines);
        this.loadedMachines = machines;
        this.elasticResultCount = success.machinesCount;
        this.applyTrafficLightFilter();
        this.changeDetector.detectChanges();
        this.handleDefaultColumnsValues(this.categoryColumns(), machines);
        this.handleMetadataColumnsValues(this.categoryColumns(), machines);
        this.startDataRetrieval(machines, true);
        this.loadingIndicator.hide();
        this.isLoading.set(false);
        this.isSearchLoading.set(false);
      }, error: (error) => {
        const message = this.translateService.instant('There was a problem loading more machines  of group') + ' ' + machineryGroupId;
        this.notificationService.error(message, error, 'MachineTileContainer', true);
        this.loadingIndicator.hide();
        this.loadedMachines = [];
        this.elasticResultCount = 0;
        this.shownMachines.set([]);
        this.resultCount.set(0);
        this.isLoading.set(false);
        this.isSearchLoading.set(false);
      }
    });
  }

  private loadExpandedDetails(machineId: number): void {
    this.store?.setExpandedLoading(true);
    this.dashboardService.getMachineDetailsFromElastic(machineId, this.organizationId, this.whiteListedMachineryGroups).subscribe({
      next: (machine: DashboardMachine | null) => {
        this.expandedMachine.update(current => machine ?? current);
        this.store?.setExpandedDetails(machine);
      },
      error: () => {
        this.store?.setExpandedDetails(null);
      },
    });

    this.elasticMetaDataService.listAllMachineMetaData(machineId).subscribe(
        {
          next: (elasticResponse: ElasticSearchResponseEntity) => {
            const machineMetadata = elasticResponse?.result?.data;
            this.expandedMetadata.set(machineMetadata);
            this.store?.setExpandedMetadata(machineMetadata);
          },
          error: () => {
            this.expandedMetadata.set(null);
            this.store?.setExpandedMetadata(null);
          },
        }
    );
  }

  private buildSearchText(searchText: string): string {
    return generateElasticSearchText(searchText);
  }

  private buildSortValue(column: string, direction: MachineListSortDirection): ElasticSearchSortValue {
    if (!column || !direction) {
      return new ElasticSearchSortValue();
    }
    if (this.isFrontendSortedColumn(column)) {
      return new ElasticSearchSortValue();
    }
    const esField = this.mapColumnToElasticSearchField(column);
    if (!esField) {
      return new ElasticSearchSortValue();
    }
    const order = direction === 'asc' ? ELASTIC_SEARCH_SORT_ORDER.ASC : ELASTIC_SEARCH_SORT_ORDER.DESC;
    return new ElasticSearchSortValue(esField, order);
  }

  private isFrontendSortedColumn(column: string): boolean {
    if (!column || column === MACHINE_NAME_COLUMN) {
      return false;
    }
    const col = this.categoryColumns().find(c => c.name === column);
    return !!col && (col.type === MachineCategoryBaseColumnTypes.SIGNAL || col.type === MachineCategoryBaseColumnTypes.METADATA);
  }

  private isSortableFrontendColumn(column: string): boolean {
    const col = this.categoryColumns().find(c => c.name === column);
    return !!col && (col.type === MachineCategoryBaseColumnTypes.METADATA || col.type === MachineCategoryBaseColumnTypes.SIGNAL && col.sortable);
  }

  private mapColumnToElasticSearchField(column: string): string | null {
    switch (column) {
      case MACHINE_NAME_COLUMN:
        return 'name';
      case MachineCategoryDefaultColumnTypes.tag.value:
        return 'machineTag';
      case MachineCategoryDefaultColumnTypes.organization.value:
        return 'tenant';
      case MachineCategoryDefaultColumnTypes.gatewayConnectivity.value:
        return null;
      case MachineCategoryDefaultColumnTypes.trafficLightStatus.value:
        return null;
      default:
        break;
    }
    const col = this.categoryColumns().find(c => c.name === column);
    if (!col) {
      return null;
    }
    if (col.type === MachineCategoryBaseColumnTypes.METADATA) {
      return null;
    }
    if (col.type === MachineCategoryBaseColumnTypes.DEFAULT) {
      return col.name;
    }
    return null;
  }

  private handleSignalPollingData(machine: DashboardMachine, dataFromPolling: any) {
    let values = [];
    if (dataFromPolling) values = dataFromPolling.values;

    if (!this.displayDetails.has(machine.id)) {
      this.displayDetails.set(machine.id, new Map<string, { value: string, style: string }>());
    }

    for (const col of this.categoryColumns()) {
      if (col.name == MachineCategoryDefaultColumnTypes.trafficLightStatus.value && dataFromPolling) {
        this.handleTrafficLightStatus(col, machine, dataFromPolling);
      }

      if (col.type !== MachineCategoryBaseColumnTypes.SIGNAL) {
        continue;
      }
      let value: string = '';

      if (values && values.hasOwnProperty(col.name)) {
        value = values[col.name];
      } else {
        value = this.NOT_AVAILABLE;
      }
      this.displayDetails.get(machine.id)?.set(col.name, {value: value, style: ''});
    }
  }

  private handleDefaultColumnsValues(categoryColumns: CategoryColumn[], machines: DashboardMachine[]) {
    const defaultColumns: CategoryColumn[] = categoryColumns.filter(c => c.type === MachineCategoryBaseColumnTypes.DEFAULT);
    if (!defaultColumns || defaultColumns.length === 0) {
      return;
    }

    for (const machine of machines) {
      this.updateDefaultColumnsForMachine(machine, defaultColumns);
    }
  }

  private updateDefaultColumnsForMachine(machine: DashboardMachine, defaultColumns: CategoryColumn[]) {
    if (!this.displayDetails.has(machine.id)) {
      this.displayDetails.set(machine.id, new Map<string, { value: string, style: string }>());
    }

    for (const column of defaultColumns) {
      const existingDetails = this.displayDetails.get(machine.id)?.get(column.name);
      let value = existingDetails?.value ?? this.NOT_AVAILABLE;
      let style = existingDetails?.style ?? '';
      switch (column.name) {
        case MachineCategoryDefaultColumnTypes.tag.value: {
          value = machine.machineTag || this.NOT_AVAILABLE;
          style = '';
          break;
        }
        case MachineCategoryDefaultColumnTypes.organization.value: {
          value = machine.tenant || String(machine.parentId);
          style = '';
          break;
        }
        case MachineCategoryDefaultColumnTypes.gatewayConnectivity.value:
        case MachineCategoryDefaultColumnTypes.trafficLightStatus.value:
          break;
        default: {
          value = this.NOT_AVAILABLE;
          style = '';
          break;
        }
      }

      this.displayDetails.get(machine.id)?.set(column.name, {value, style});
    }
  }

  private updateGatewayConnectivity(machine: DashboardMachine, categoryColumns: CategoryColumn[], gateways: any[]) {
    if (!this.displayDetails.has(machine.id)) {
      this.displayDetails.set(machine.id, new Map<string, { value: string, style: string }>());
    }

    const gatewayStatusColumn = categoryColumns.find(c => c.name === MachineCategoryDefaultColumnTypes.gatewayConnectivity.value);
    if (!gatewayStatusColumn) {
      return;
    }
    const gatewaysStatus: GATEWAYS_AGGREGATED_STATUS = this.machineDetailsService.determineAggregatesGatewaysStatus(gateways, false);
    let style: string;
    let value: string;
    switch (gatewaysStatus) {
      case GATEWAYS_AGGREGATED_STATUS.CONNECTED:
        style = 'badge badge-success';
        value = 'Connected';
        break;
      case GATEWAYS_AGGREGATED_STATUS.PARTIALLY_DISCONNECTED:
        style = 'badge badge-warning';
        value = 'Partially disconnected';
        break;
      case GATEWAYS_AGGREGATED_STATUS.DISCONNECTED:
        style = 'badge badge-danger';
        value = 'Disconnected';
        break;
      case GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION:
        style = 'badge badge-danger';
        value = 'Misconfiguration';
        break;
      case GATEWAYS_AGGREGATED_STATUS.NO_GATEWAY_CONNECTORS:
        style = 'badge badge-secondary';
        value = 'No gateway connectors';
        break;
      case GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS:
        style = 'badge badge-secondary';
        value = 'No gateways';
        break;
      default:
        style = 'badge badge-dark';
        value = this.UNKNOWN;
        break;
    }

    this.displayDetails.get(machine.id)?.set(gatewayStatusColumn.name, {
      value: this.translate.instant(value),
      style: style
    });
  }

  private handleMetadataColumnsValues(categoryColumns: CategoryColumn[], machines: DashboardMachine[]) {
    const metaDataFields: CategoryColumn[] = categoryColumns.filter(c => c.type === MachineCategoryBaseColumnTypes.METADATA);
    if (!metaDataFields || metaDataFields.length === 0) {
      return;
    }

    for (const machine of machines) {
      this.elasticMetaDataService.listAllMachineMetaData(machine.id).subscribe({
        next: (elasticResponse: ElasticSearchResponseEntity) => {
          const machineMetadata = elasticResponse?.result?.data;
          this.updateMetadataValuesForMachine(machine.id, metaDataFields, machineMetadata);
        }, error: () => {
          this.updateMetadataValuesForMachine(machine.id, metaDataFields, null);
        }
      });
    }
  }

  private updateMetadataValuesForMachine(machineId: number, metaDataFields: CategoryColumn[], machineMetadata: MetaData[] | null) {
    if (!this.displayDetails.has(machineId)) {
      this.displayDetails.set(machineId, new Map<string, { value: string, style: string }>());
    }
    for (const column of metaDataFields) {
      if (column.type != MachineCategoryBaseColumnTypes.METADATA) {
        continue;
      }
      const value: string = this.getMetadataValueForColumn(column.name, machineMetadata);
      this.displayDetails.get(machineId)?.set(column.name, {value: value, style: ''});

    }
  }

  private getMetadataValueForColumn(columnName: string, machineMetadata: MetaData[] | null): string {
    if (!machineMetadata || machineMetadata.length === 0) {
      return this.NOT_AVAILABLE;
    }

    const metadataField = machineMetadata.find(m => m.key === columnName);
    if (!metadataField || !metadataField.value) {
      return this.NOT_AVAILABLE;
    }

    return metadataField.value;
  }

  private startPolling(machine: DashboardMachine): Observable<any> {
    if (this.refreshInterval == null || !this.refreshInterval) {
      this.refreshInterval = this.REFRESH_INTERVAL;
    }
    const pollingRequestParameters: RealtimePollingRequestParameters = new RealtimePollingRequestParameters({
      machineId: machine.id,
      organizationId: machine.parentId,
      refresh: this.refreshInterval,
      latestOnly: true
    });

    this.stopDataRetrievalForMachine(machine.id);
    return this.pollingService.start(this.GET_SIGNAL_DATA_URL_SUFFIX, pollingRequestParameters, machine.id + '').pipe(takeUntil(this.destroy$));
  }

  private handleTrafficLightStatus(col: CategoryColumn, machine: DashboardMachine, dataFromPolling: any) {

    if (col.name === MachineCategoryDefaultColumnTypes.trafficLightStatus.value) {
      let style: string;
      let value: string;
      const status = dataFromPolling?.trafficLightStatus?.status ?? TRAFFIC_LIGHT_STATUS.CANNOT_EVALUATE;
      this.trafficLightStatuses.set(machine.id, status);
      switch (status) {
        case TRAFFIC_LIGHT_STATUS.RUNNING:
          style = 'badge badge-success';
          value = 'Running';
          break;
        case TRAFFIC_LIGHT_STATUS.WARNING:
          style = 'badge badge-warning';
          value = 'Warning';
          break;
        case TRAFFIC_LIGHT_STATUS.FAILURE:
          style = 'badge badge-danger';
          value = 'Error';
          break;
        case TRAFFIC_LIGHT_STATUS.INFO:
          style = 'badge badge-secondary';
          value = 'Info';
          break;
        case TRAFFIC_LIGHT_STATUS.OFFLINE:
          style = 'badge badge-dark';
          value = 'Offline';
          break;
        case TRAFFIC_LIGHT_STATUS.CANNOT_EVALUATE:
          style = 'badge badge-dark';
          value = 'Cannot evaluate';
          break;
        default:
          style = 'badge badge-dark';
          value = 'Unknown';
          break;
      }
      this.displayDetails.get(machine.id)?.set(col.name, {value: this.translate.instant(value), style: style});
      if (this.selectedTrafficLightFilter()) {
        this.applyTrafficLightFilter();
      }
    }
  }
}
